<template>
  <div class="EventsOverviewWrapper">
    <div class="grid">
      <div class="event-overview-header flex align-end">
        <div class="col-7-12 sm-col-1-1">
          <UIHeadingType
            class="mb-xxxlg"
            :heading-type="content.seoHeadingtype"
            :title="content.title"
          />
          <div v-html="content.text" />
        </div>
        <div class="col-4-12 push-1-12 sm-col-1-1 sm-push-0 sm-ptb-xlg">
          <div class="search-input-wrapper">
            <input
              id="events-search-input"
              v-model="searchString"
              class="search-input"
              :aria-label="filterLabel"
              :placeholder="filterLabel"
              @input="handleSearch($event.target.value)"
            />
            <UIIconComponent
              icon="search"
              class="search-icon"
            />
          </div>
        </div>
      </div>

      <div class="contentblock-margin col-1-1 no-p">
        <div class="col-1-4 sm-col-1-1">
          <UIEventsFilterWrapper
            :available-filters="availableFilters"
            :initial-date-value="[startAt, endAt]"
            :total-results="pagination?.count"
            @update-date="handleDateUpdate"
            @update-filter="updateFilterValueWrapper"
            @delete-filter="clearFilterSelectionWrapper"
            @clear-all-filters="clearAllFilters"
          />
        </div>

        <div class="events-wrapper sm-col-1-1 col-3-4 no-p">
          <UIModuleActiveFilterChips
            :chips="activeFilters"
            @delete-chip="clearFilterSelectionWrapper"
            @clear-all="clearAllFilters"
          />
          <template
            v-for="(chunk, idx) in eventsSplit"
            :key="idx"
          >
            <UIEventsOverviewListItem
              v-for="(event, eventIdx) in chunk.events"
              :key="`${event.id}-${eventIdx}`"
              :event="event"
            />

            <UIEventsOverviewListBanner
              v-if="chunk.banner"
              :content="chunk.banner.content"
            />
          </template>

          <div
            v-if="!events?.length"
            class="col-1-1"
          >
            <p>Geen evenementen gevonden</p>

            <UIHoverAnimationButton
              class="btn btn-primary mt-sm"
              name="Wis filters"
              @click="clearAllFilters"
            />
          </div>
        </div>

        <BaseArticlePagination
          v-if="eventsResponse"
          class="pagination"
          :articles-per-page="pageSize"
          :article-count="eventsResponse.pagination?.count"
          :current-page="currentPage"
          :page-count="eventsResponse.pagination?.pages"
          @update:current-page="updateCurrentPage"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import type { EventFilter, EventFilterOption } from '@tc8l/nuxt-overview'
  import type { ElocusPage } from '@tc8l/nuxt-base'
  import type { RelatedArticlesContent } from '~/types/contentblocks'
  const { convertItems, trackSearch } = useTracking()

  const props = defineProps<{
    content: RelatedArticlesContent
    id?: string
    class?: string
    page?: string
    settings?: ElocusPage
    contentblockIdx?: string | number
  }>()

  const { clearFilterSelection, getSelectedFilterObjects } = useFilterHelpers()

  const activeFilters = computed(() => getSelectedFilterObjects(availableFilters.value ?? []))

  const pageSize = 12

  const filterLabel = 'Zoeken in agenda'

  const filterOptions = await getEventFilters()
  const {
    events,
    availableFilters,
    fetchEvents,
    updateFilterValue,
    clearFilterValue,
    eventsResponse,
    setExcludedFilters,
    setItemsExcluded,
    startAt,
    endAt,
    currentPage,
    setCurrentPage,
    setPageSize,
    searchString,
    setSearchString,
    updateSearch,
    updateClearFilterValue,
    updateDate,
    updateCurrentPage,
  } = useEventOverview(filterOptions)

  const excludedFilters = computed(() => {
    const excluded = {}
    if (props.content.excludedLabelList != null && props.content.excludedLabelList.length > 0) {
      excluded.eventAttribute = props.content.excludedLabelList?.map((l) => l._id) ?? []
    }

    if (props.content.excludedLocationList != null && props.content.excludedLocationList.length > 0) {
      excluded.location = props.content.excludedLocationList?.map((l) => l._id) ?? []
    }

    if (props.content.excludedGenreList != null && props.content.excludedGenreList.length > 0) {
      excluded.genre = props.content.excludedGenreList?.map((l) => l._id) ?? []
    }

    return excluded
  })

  const excludedItems = computed(() => {
    return props.content.excludedEventList?.map((e) => e._id) ?? []
  })

  setExcludedFilters(excludedFilters.value)
  setItemsExcluded(excludedItems.value)
  setPageSize(pageSize)

  await fetchEvents()

  onMounted(() => {
    convertItems({ type: 'events', payload: events.value }, (currentPage.value - 1) * pageSize)
  })

  const stopWatcher = watch(events, (newEvents) => {
    convertItems({ type: 'events', payload: newEvents }, (currentPage.value - 1) * pageSize)
  })

  onUnmounted(() => {
    stopWatcher()
  })

  const pagination = computed(() => eventsResponse.value?.pagination)

  const BANNER_SPACING = 5

  const eventsSplit = computed(() => {
    if (!events.value) return []
    const chunkSize = BANNER_SPACING
    const result = []

    // Create event chunks
    for (let i = 0; i < events.value.length; i += chunkSize) {
      const eventChunk = events.value.slice(i, i + chunkSize)
      const chunkIndex = Math.floor(i / chunkSize)

      const chunk = {
        events: eventChunk,
      }

      const hasMoreEvents = i + chunkSize < events.value.length
      if (hasMoreEvents && props.content.bannerList && props.content.bannerList.length > 0) {
        const bannerIndex = chunkIndex % props.content.bannerList.length
        chunk.banner = props.content.bannerList[bannerIndex]
      }

      result.push(chunk)
    }

    return result
  })

  async function clearAllFilters() {
    availableFilters.value?.forEach((filter) => {
      clearFilterValue(filter.value, filter.idx)
    })
    setCurrentPage(1)
    setSearchString('')

    updateDate(null, null)
  }

  function updateFilterValueWrapper(filter: EventFilter, option: EventFilterOption) {
    updateFilterValue(filter.value, option.label)
  }

  function clearFilterSelectionWrapper(filter: EventFilter) {
    clearFilterSelection(filter, updateClearFilterValue)
  }

  function handleDateUpdate(startDate: Date | null, endDate: Date | null) {
    updateDate(startDate, endDate)
  }

  let searchTimeout: ReturnType<typeof setTimeout>

  function handleSearch(searchTerm: string) {
    updateSearch(searchTerm)
    clearTimeout(searchTimeout)

    if (!searchTerm.trim()) return

    searchTimeout = setTimeout(() => {
      trackSearch(searchTerm)
    }, 800)
  }
</script>

<style scoped lang="scss">
  .events-wrapper {
    display: flex;
    flex-direction: column;
    row-gap: calc($gutter * 2);
  }

  .event-overview-header {
    padding-top: calc($contentblock-margin * 2);
  }

  .contentblock-margin {
    padding: $contentblock-margin 0;
  }

  .mobile-filters {
    display: none;
  }

  .search-input-wrapper {
    display: flex;
    height: 5.6rem;
    padding: var(--padding-xs) var(--padding-sm);
    align-items: center;
    gap: var(--spacing-sm, 1.6rem);
    border-radius: var(--border-radius-md);
    border: 2px solid #e3e3e3;
    background-color: $color-accent;

    position: relative;
    width: 100%;
  }

  .search-input {
    width: 100%;
    border: none;
    background-color: transparent;
    outline: none;
    font-size: 1.6rem;
    line-height: 1;

    &::placeholder {
      opacity: 0.3;
    }
  }

  :deep(.pagination) {
    .pagination-btn {
      &:hover {
        background-color: $black;
        color: $white;
      }
    }

    .pagination-btn.active {
      background-color: $black;
      color: $white;

      &:hover {
        background-color: $black;
        color: $white;
      }
    }
  }

  @include media(sm) {
    .events-wrapper {
      row-gap: var(--margin-xlg);
    }

    .event-overview-header {
      padding-top: $contentblock-margin;
    }

    .contentblock-margin {
      padding-top: 0;

      .FilterWrapper {
        padding-bottom: $contentblock-margin;
      }
    }

    .mobile-filter-button {
      background-color: $black;
      color: $white;
      border: none;
      border-radius: 100%;
      width: 36px;
      height: 36px;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      padding: 12px;

      .filter-icon {
        font-size: 2rem;
        color: $white;
      }
    }

    .mobile-filter-badge {
      background-color: $white;
      color: $black;
      padding: 8px 16px;
      border-radius: 20px;
      border: 1px solid $black;
      display: inline-flex;
      align-items: center;
      cursor: pointer;
      font-size: 1.4rem;

      .filter-icon {
        margin-left: 8px;
        font-size: 1.8rem;
        color: $black;
      }

      &.active-filter-badge {
        background-color: $black;
        color: $white;
        border-width: 2px;
        font-weight: bold;

        .filter-icon {
          color: $white;
        }
      }
    }

    .mobile-filters-modal {
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-color: rgba(0, 0, 0, 0.5);
      display: flex;
      justify-content: center;
      align-items: flex-end;
      z-index: 1000;
    }

    .modal-content {
      background-color: white;
      width: 100%;
      max-height: 80vh;
      transform: translateY(100%);
      animation: slideUp 0.2s forwards;
      display: flex;
      flex-direction: column;
      position: relative;
    }

    @keyframes slideUp {
      from {
        transform: translateY(100%);
      }

      to {
        transform: translateY(0);
      }
    }

    .modal-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      border-bottom: 1px solid $lightgrey;
      position: sticky;
      top: 0;
      background-color: white;
      z-index: 1;
      padding: 16px 24px;

      .modal-title {
        font-weight: 700;
      }

      .modal-close {
        color: $black;
        font-size: 2.4rem;
      }
    }

    .modal-body {
      padding: 16px 24px;
      flex: 1;
      overflow-y: auto;
    }

    .modal-footer {
      position: sticky;
      bottom: 0;
      left: 0;
      width: 100%;
      display: flex;
      justify-content: flex-end;
      gap: 16px;
      border-top: 1px solid $lightgrey;
      background-color: white;
      padding: 16px 24px;
      z-index: 1;

      button {
        margin: 0;
      }

      .btn-confirm {
        width: 100%;
      }
    }

    .desktop-filters {
      display: none;
    }

    .mobile-filters {
      display: flex;
      overflow-x: auto;
      white-space: nowrap;
      width: 100%;
      padding: 10px 0;
      -webkit-overflow-scrolling: touch;

      > * {
        margin-right: 10px;

        &:last-child {
          margin-right: 0;
        }
      }
    }
  }
</style>
